'use client';

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { languages } from '@/utils/languages'

declare global {
  interface Window {
    google: {
      translate: {
        TranslateElement: any;
        Element: any;
      };
    };
  }
}

const LanguageDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    setSelectedLang(langCode);
    setIsOpen(false);

    // Directly manipulate the Google Translate dropdown
    const googleCombo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
    if (googleCombo) {
      googleCombo.value = langCode;
      googleCombo.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // If Google Translate hasn't initialized yet, wait and try again
      const waitForGoogle = setInterval(() => {
        const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement;
        if (combo) {
          combo.value = langCode;
          combo.dispatchEvent(new Event('change', { bubbles: true }));
          clearInterval(waitForGoogle);
        }
      }, 100);

      // Clear interval after 5 seconds if Google Translate hasn't loaded
      setTimeout(() => clearInterval(waitForGoogle), 5000);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="ghost" 
        size="sm" 
        className="text-[10px] h-5 px-2 py-0 text-white border border-white rounded-full hover:bg-white hover:text-[#9b59b6] transition-all duration-200 transform hover:scale-105"
        onClick={() => setIsOpen(!isOpen)}
      >
        {languages.find(lang => lang.code === selectedLang)?.flag} {' '}
        {selectedLang.toUpperCase()} <ChevronDown className="ml-0.5 h-2 w-2" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg z-50">
          <div className="py-1">
            {languages.map((language) => (
              <button
                key={language.code}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                onClick={() => handleLanguageChange(language.code)}
              >
                <span>{language.flag}</span>
                <span>{language.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;